import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ConnectionsHelpDialog: React.FC<Props> = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HelpOutlineIcon fontSize="small" color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            How to add your own connection
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>

    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Saved connections let you pick your connection string — no copy-pasting connection strings.
        Here's how to get yours added:
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Step number={1} title="Grab your connection string">
        Either from your user secrets or via the Azure portal
      </Step>

      <Step number={2} title="Add to Key Vault">
        Add it to Azure key vault in the test env <code>wisr-test-env-keyvault</code>. Please follow
        the same naming convention <strong>Bus-Intercept-ServiceBus-Connection-YourName</strong>
      </Step>

      <Step number={3} title="Add App setting to Bus.Intercept.Api">
        Once it's been added to Key Vault go to{" "}
        <a href="https://dev.azure.com/directmoney/WISR/_git/Wisr.Bus.Intercept.Api">
          Wisr.Bus.Intercept.Api
        </a>
        and add your secret URI and follow the existing pattern. That's it !
      </Step>

      <Step number={4} title="Raise PR - Done !">
        Once deployed its all sorted, no more having to go back for your connection string.
      </Step>
    </DialogContent>

    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} variant="contained" disableElevation>
        Got it
      </Button>
    </DialogActions>
  </Dialog>
);

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: "primary.main",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "0.8rem",
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {number}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
